import { usePrivateToast } from "@/shared/usePrivateToast";
import { usePrivateApi } from "@/shared/usePrivateApi";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ConfirmModal } from "@a2z/ui";

import { endpoints } from "@/lib/api/endpoints";
import { useTranslate } from "@/lib/i18n";
import { queryKeys } from "@/shared/queries";
import { useErrorMessage } from "@/shared/errors";
import { hasPermission, PERMISSIONS, useSession } from "@/modules/auth/useSession";
import type { MenuItem } from "@/lib/api/types";

/**
 * 86'ING, FOR EVERY TILL SCREEN THAT SHOWS A MENU (§5.8, D32).
 *
 * §5.8 says the act happens AT THE TILL, and the till is two screens: the
 * table, where a waiter stages the first round before a bill exists, and the
 * bill itself. Only the second had the control, so "we are out of fish" could
 * not be recorded until somebody opened a cheque — at exactly the moment the
 * kitchen shouts it, which is between tables. The way round was to open a bill
 * nobody wanted or to type the URL of the kitchen's own 86 screen.
 *
 * A hook rather than a second copy: the pair is a toggle AND the confirm that
 * names how far it reaches, and a copied confirm is one that stops being
 * maintained. The caller spreads `boardProps` onto `MenuBoard` and renders
 * `confirm` beside it.
 */
export function useMenuAvailability() {
  const { apiFetch } = usePrivateApi();
  const toast = usePrivateToast();
  const t = useTranslate();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();
  const { data: session } = useSession();

  /*
   * The endpoint takes EITHER permission (`MENU_MANAGE` or
   * `MENU_AVAILABILITY`, an either/or enforced in the service because
   * `@RequirePermission` ANDs its arguments), so the gate mirrors that rather
   * than checking the cashier's grant alone — an owner standing at the till
   * holds MANAGE and not necessarily AVAILABILITY.
   */
  const canSetAvailability =
    hasPermission(session, PERMISSIONS.MENU_AVAILABILITY) ||
    hasPermission(session, PERMISSIONS.MENU_MANAGE);

  /*
   * Two taps on purpose: the control is a 44px corner button on a tile in a
   * grid held in one hand mid-service. It is the same posture the settle
   * sheet's write-off row takes, for the same reason.
   */
  const [target, setTarget] = useState<MenuItem | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const apply = async (item: MenuItem) => {
    setBusyId(item.id);
    try {
      await apiFetch(endpoints.restaurant.menuItemAvailability(item.id), {
        method: "PUT",
        body: { isAvailable: !item.isAvailable },
      });
      // The menu query is what flips the tile: the board renders
      // `item.isAvailable` straight from that cache.
      await queryClient.invalidateQueries({ queryKey: queryKeys.menuItems });
      await queryClient.invalidateQueries({ queryKey: queryKeys.kitchenAvailability });
      toast.success(
        item.isAvailable
          ? t((m) => m.pos.menuMarkedSoldOutToast)
          : t((m) => m.pos.menuMarkedAvailableToast),
      );
      setTarget(null);
    } catch (failure) {
      toast.error(errorMessage(failure));
    } finally {
      setBusyId(null);
    }
  };

  const isBack = target?.isAvailable === false;

  return {
    canSetAvailability,
    /** Spread onto `MenuBoard`. Independent of the bill's own state and of
     *  `canAppend`: a closed bill, or a waiter who may not append, does not
     *  change whether the kitchen has run out of fish. */
    boardProps: {
      onToggleAvailability: canSetAvailability ? setTarget : undefined,
      togglingProductId: busyId,
    },
    /* The confirm names the item and says how far the change reaches — every
       till in the store and the diners' QR menu — because a control small
       enough to sit on a tile corner must not hide how store-wide it is.
       Rendered unconditionally so it keeps its subject through its own closing
       animation. */
    confirm: (
      <ConfirmModal
        isOpen={target !== null}
        onClose={() => setTarget(null)}
        onConfirm={() => {
          if (target) void apply(target);
        }}
        testId="menu-availability-confirm"
        title={
          isBack
            ? t((m) => m.pos.menuUnmarkSoldOutShort)
            : t((m) => m.pos.menuMarkSoldOutShort)
        }
        description={
          isBack
            ? t((m) => m.pos.menuUnmarkSoldOutBody, { name: target?.nameAr ?? "" })
            : t((m) => m.pos.menuMarkSoldOutBody, { name: target?.nameAr ?? "" })
        }
        confirmLabel={
          isBack
            ? t((m) => m.pos.menuUnmarkSoldOutShort)
            : t((m) => m.pos.menuMarkSoldOutShort)
        }
        cancelLabel={t((m) => m.common.cancel)}
        closeLabel={t((m) => m.common.close)}
        isSubmitting={busyId !== null}
        /* `caution`, the middle rung — and it is the same tone the kitchen's
           own 86 confirm wears, which it did not used to be. The three-question
           test settles it: the same person can undo this in one tap (Q1 yes),
           but it reaches every till in the store and the diners' QR menu before
           they could (Q2 yes) — so it is neither `danger`, which belongs to
           money leaving a ticket, nor `primary`, which would call 86'ing a dish
           mid-service the recommended thing to do. The body names the reversal.
           Until this rung existed the two surfaces disagreed about the SAME
           act: `primary` here, `danger` in `KitchenAvailabilityPage`. */
        tone="caution"
      />
    ),
  };
}
